import {
  CONVERSATION_PRIORITY_ORDER,
  MESSAGE_TYPE,
} from 'shared/constants/messages';
import {
  conversationMatchesUnassigned,
  getConversationParticipantUserIds,
} from 'dashboard/components/widgets/conversation/conversationQuickFilters';

export const findPendingMessageIndex = (chat, message) => {
  const { echo_id: tempMessageId } = message;
  return chat.messages.findIndex(
    m => m.id === message.id || m.id === tempMessageId
  );
};

// Valid Unix-ish activity timestamps for list ordering. Reject null/empty/NaN
// and non-positive values so a bad payload cannot falsely bump activity.
export const isValidConversationActivityAt = value => {
  if (value === null || value === undefined || value === '') return false;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0;
};

// Canonical activity for list reorder after a successful send.
// Prefer conversation.last_activity_at from the server payload; fall back to
// message created_at only when it is a valid positive timestamp.
export const resolveMessageConversationActivityAt = messageData => {
  const fromConversation = messageData?.conversation?.last_activity_at;
  if (isValidConversationActivityAt(fromConversation)) {
    return Number(fromConversation);
  }

  const fromCreatedAt = messageData?.created_at;
  if (isValidConversationActivityAt(fromCreatedAt)) {
    return Number(fromCreatedAt);
  }

  return null;
};

// ActionCable message.created: keep CSW fields in sync with MessageWindowService.
export const buildMessagingWindowPatchFromMessage = message => {
  const conversationId = message?.conversation_id;
  if (!conversationId) return null;

  const patch = message?.conversation;
  if (patch && patch.can_reply !== undefined) {
    return {
      conversationId,
      canReply: patch.can_reply,
      messaging_window_expires_at: patch.messaging_window_expires_at,
      messaging_window_kind: patch.messaging_window_kind,
      messaging_window_fep_expires_at: patch.messaging_window_fep_expires_at,
    };
  }

  if (message?.message_type !== MESSAGE_TYPE.INCOMING || message?.private) {
    return null;
  }

  const createdAt = Number(message.created_at);
  if (!Number.isFinite(createdAt) || createdAt <= 0) return null;

  return {
    conversationId,
    canReply: true,
    messaging_window_expires_at: createdAt + 24 * 60 * 60,
  };
};

// SiteUp: "active" agrega tudo que está em andamento (aberto + pendente + adiado),
// espelhando ACTIVE_STATUSES do ConversationFinder no backend.
const ACTIVE_STATUSES = ['open', 'pending', 'snoozed'];

export const filterByStatus = (chatStatus, filterStatus) => {
  if (filterStatus === 'all') return true;
  if (filterStatus === 'active') return ACTIVE_STATUSES.includes(chatStatus);
  // SiteUp: multi-status combinado (ex.: "open,pending,resolved") usado pelos
  // filtros rápidos configuráveis (Minhas / Não atribuídas).
  if (typeof filterStatus === 'string' && filterStatus.includes(',')) {
    return filterStatus.split(',').includes(chatStatus);
  }
  return chatStatus === filterStatus;
};

export const filterByInbox = (shouldFilter, inboxId, chatInboxId) => {
  // SiteUp: inboxId pode ser um ARRAY (multi-selecao de caixas no filtro rapido)
  // ou um escalar. Sem tratar o array, Number([5,6]) === chatInboxId vira
  // NaN === id => sempre false => a lista esvazia e o scroll infinito dispara
  // fetch em loop ("a tela cai e volta"). Com array, casa se a conversa esta em
  // QUALQUER caixa selecionada; array vazio = nao filtra (todas as caixas).
  if (Array.isArray(inboxId)) {
    if (inboxId.length === 0) return shouldFilter;
    const isOnInbox = inboxId.map(Number).includes(Number(chatInboxId));
    return isOnInbox && shouldFilter;
  }
  const isOnInbox = Number(inboxId) === chatInboxId;
  return inboxId ? isOnInbox && shouldFilter : shouldFilter;
};

export const filterByTeam = (shouldFilter, teamId, chatTeamId) => {
  const isOnTeam = Number(teamId) === chatTeamId;
  return teamId ? isOnTeam && shouldFilter : shouldFilter;
};

export const filterByLabel = (shouldFilter, labels, chatLabels) => {
  const isOnLabel = labels.every(label => chatLabels.includes(label));
  return labels.length ? isOnLabel && shouldFilter : shouldFilter;
};
export const filterByUnattended = (
  shouldFilter,
  conversationType,
  firstReplyOn,
  waitingSince
) => {
  return conversationType === 'unattended'
    ? (!firstReplyOn || !!waitingSince) && shouldFilter
    : shouldFilter;
};

// SiteUp: Atalhos toggle — when OFF, Pendentes (unattended) hides threads with
// no human owner (same contract as Conversation.without_human_owner).
export const filterByIncludeUnassignedInPendentes = (
  shouldFilter,
  conversationType,
  includeUnassignedInPendentes,
  conversation
) => {
  if (conversationType !== 'unattended') return shouldFilter;
  if (includeUnassignedInPendentes !== false) return shouldFilter;
  return shouldFilter && !conversationMatchesUnassigned(conversation);
};

export const filterByUnread = (shouldFilter, conversationType, unreadCount) => {
  return conversationType === 'unread'
    ? Number(unreadCount) > 0 && shouldFilter
    : shouldFilter;
};

// SiteUp: o chip "Agendadas" filtra por conversa com mensagem agendada pendente.
// O cliente re-filtra o cache compartilhado, então precisa do sinal
// `has_scheduled_message` (serializer/push). Espelha o filterByUnread — assim o
// chip se auto-corrige no tempo real (websocket) em vez de mostrar cards que
// nao tem agendamento (ou manter card de agendamento ja cancelado).
export const filterByScheduled = (
  shouldFilter,
  conversationType,
  hasScheduledMessage
) => {
  return conversationType === 'scheduled'
    ? !!hasScheduledMessage && shouldFilter
    : shouldFilter;
};

// SiteUp: chip Automação — membership em additional_attributes.mass_automation.active
export const filterByAutomation = (
  shouldFilter,
  conversationType,
  massAutomationActive
) => {
  return conversationType === 'automation'
    ? !!massAutomationActive && shouldFilter
    : shouldFilter;
};

export const isMassAutomationActive = conversation => {
  const active = conversation?.additional_attributes?.mass_automation?.active;
  return active === true || active === 'true';
};

const isPublicIncomingOrOutgoingMessage = message => {
  if (!message || message.private === true) return false;
  const messageType = Number(message.message_type);
  return (
    messageType === MESSAGE_TYPE.INCOMING ||
    messageType === MESSAGE_TYPE.OUTGOING
  );
};

// Espelho de Conversation.awaiting_reply_sql: a última mensagem pública
// incoming/outgoing ainda é do cliente. Payload de lista/websocket traz
// last_non_activity_message; conversation.created só traz messages[].
export const resolveLastPublicIncomingOrOutgoingMessage = conversation => {
  const lastNonActivity = conversation?.last_non_activity_message;
  if (isPublicIncomingOrOutgoingMessage(lastNonActivity)) {
    return lastNonActivity;
  }

  const messages = Array.isArray(conversation?.messages)
    ? conversation.messages
    : [];
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (isPublicIncomingOrOutgoingMessage(messages[index])) {
      return messages[index];
    }
  }

  return null;
};

export const conversationIsAwaitingHumanReply = conversation => {
  const lastMessage = resolveLastPublicIncomingOrOutgoingMessage(conversation);
  if (lastMessage) {
    return Number(lastMessage.message_type) === MESSAGE_TYPE.INCOMING;
  }

  const waitingSince = Number(conversation?.waiting_since);
  return Number.isFinite(waitingSince) && waitingSince > 0;
};

const conversationHasUnreadCustomerMessage = conversation =>
  Number(conversation?.unread_count) > 0;

const isCurrentUserAssignee = (conversation, currentUserId) => {
  const userId = Number(currentUserId);
  if (!userId) return false;
  return Number(conversation?.meta?.assignee?.id) === userId;
};

const isCurrentUserParticipant = (conversation, currentUserId) => {
  const userId = Number(currentUserId);
  if (!userId) return false;

  const participantIds = getConversationParticipantUserIds(conversation);
  if (participantIds?.includes(userId)) return true;

  const metaParticipants = conversation?.meta?.participants;
  if (!Array.isArray(metaParticipants)) return false;
  return metaParticipants.some(
    participant => Number(participant?.id ?? participant?.user_id) === userId
  );
};

// Espelho de ConversationFinder#apply_inbox_list_exclusions: campanha/automação
// em massa só entra nas listas normais se for minha, se eu participar, se o
// cliente estiver aguardando resposta humana, ou se houver incoming não lida.
export const filterByMassAutomationInboxExclusion = (
  shouldFilter,
  conversationType,
  conversation,
  currentUserId
) => {
  if (!shouldFilter) return false;
  if (conversationType === 'automation') return shouldFilter;
  if (!isMassAutomationActive(conversation)) return shouldFilter;

  return (
    isCurrentUserAssignee(conversation, currentUserId) ||
    isCurrentUserParticipant(conversation, currentUserId) ||
    conversationIsAwaitingHumanReply(conversation) ||
    conversationHasUnreadCustomerMessage(conversation)
  );
};

export const applyPageFilters = (conversation, filters) => {
  const {
    inboxId,
    status,
    labels = [],
    teamId,
    conversationType,
    includeUnassignedInPendentes,
    currentUserId,
  } = filters;
  const {
    status: chatStatus,
    inbox_id: chatInboxId,
    labels: chatLabels = [],
    meta = {},
    first_reply_created_at: firstReplyOn,
    waiting_since: waitingSince,
    unread_count: unreadCount,
    has_scheduled_message: hasScheduledMessage,
  } = conversation;
  const team = meta.team || {};
  const { id: chatTeamId } = team;
  const massAutomationActive = isMassAutomationActive(conversation);

  let shouldFilter = filterByStatus(chatStatus, status);
  shouldFilter = filterByInbox(shouldFilter, inboxId, chatInboxId);
  shouldFilter = filterByTeam(shouldFilter, teamId, chatTeamId);
  shouldFilter = filterByLabel(shouldFilter, labels, chatLabels);
  shouldFilter = filterByUnattended(
    shouldFilter,
    conversationType,
    firstReplyOn,
    waitingSince
  );
  shouldFilter = filterByIncludeUnassignedInPendentes(
    shouldFilter,
    conversationType,
    includeUnassignedInPendentes,
    conversation
  );
  shouldFilter = filterByUnread(shouldFilter, conversationType, unreadCount);
  shouldFilter = filterByAutomation(
    shouldFilter,
    conversationType,
    massAutomationActive
  );
  shouldFilter = filterByScheduled(
    shouldFilter,
    conversationType,
    hasScheduledMessage
  );
  shouldFilter = filterByMassAutomationInboxExclusion(
    shouldFilter,
    conversationType,
    conversation,
    currentUserId
  );

  return shouldFilter;
};

/**
 * Filters conversations based on user role and permissions
 *
 * @param {Object} conversation - The conversation object to check permissions for
 * @param {string} role - The user's role (administrator, agent, etc.)
 * @param {Array<string>} permissions - List of permission strings the user has
 * @param {number|string} currentUserId - The ID of the current user
 * @returns {boolean} - Whether the user has permissions to access this conversation
 */
export const applyRoleFilter = (
  conversation,
  role,
  permissions,
  currentUserId
) => {
  // the role === "agent" check is typically not correct on it's own
  // the backend handles this by checking the custom_role_id at the user model
  // here however, the `getUserRole` returns "custom_role" if the id is present,
  // so we can check the role === "agent" directly
  if (['administrator', 'agent'].includes(role)) {
    return true;
  }

  // Check for full conversation management permission
  if (permissions.includes('conversation_manage')) {
    return true;
  }

  const conversationAssignee = conversation.meta.assignee;
  const isUnassigned = !conversationAssignee;
  const isAssignedToUser = conversationAssignee?.id === currentUserId;

  // Check unassigned management permission
  if (permissions.includes('conversation_unassigned_manage')) {
    return isUnassigned || isAssignedToUser;
  }

  // Check participating conversation management permission
  if (permissions.includes('conversation_participating_manage')) {
    return isAssignedToUser;
  }

  return false;
};

const SORT_OPTIONS = {
  last_activity_at_asc: ['sortOnLastActivityAt', 'asc'],
  last_activity_at_desc: ['sortOnLastActivityAt', 'desc'],
  created_at_asc: ['sortOnCreatedAt', 'asc'],
  created_at_desc: ['sortOnCreatedAt', 'desc'],
  priority_asc: ['sortOnPriority', 'asc'],
  priority_desc: ['sortOnPriority', 'desc'],
  waiting_since_asc: ['sortOnWaitingSince', 'asc'],
  waiting_since_desc: ['sortOnWaitingSince', 'desc'],
  // Fila do Modo Foco: janela de 24h primeiro. Nao usar no filtro
  // "Aguardando mais tempo" (esse e waiting_since_asc, espera pura).
  waiting_window_asc: ['sortOnWaitingWindow', 'asc'],
  priority_desc_created_at_asc: ['sortOnPriorityCreatedAt', 'desc'],
};
const sortAscending = (valueA, valueB) => valueA - valueB;
const sortDescending = (valueA, valueB) => valueB - valueA;

const getSortOrderFunction = sortOrder =>
  sortOrder === 'asc' ? sortAscending : sortDescending;

// Janela de mensagem livre da API oficial do WhatsApp. Numa conversa que está
// aguardando, a última atividade é a mensagem do lead — ou seja, o início da
// janela. Os timestamps da lista são epoch em SEGUNDOS.
export const MESSAGING_WINDOW_SECONDS = 24 * 60 * 60;

export const isInsideMessagingWindow = (
  chat,
  nowSeconds = Date.now() / 1000
) => {
  const lastActivity = Number(chat?.last_activity_at || 0);
  if (!lastActivity) return false;

  return nowSeconds - lastActivity < MESSAGING_WINDOW_SECONDS;
};

const sortConfig = {
  sortOnLastActivityAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.last_activity_at, b.last_activity_at),

  sortOnCreatedAt: (a, b, sortDirection) =>
    getSortOrderFunction(sortDirection)(a.created_at, b.created_at),

  sortOnPriority: (a, b, sortDirection) => {
    const DEFAULT_FOR_NULL = sortDirection === 'asc' ? 5 : 0;

    const p1 = CONVERSATION_PRIORITY_ORDER[a.priority] || DEFAULT_FOR_NULL;
    const p2 = CONVERSATION_PRIORITY_ORDER[b.priority] || DEFAULT_FOR_NULL;

    return getSortOrderFunction(sortDirection)(p1, p2);
  },

  sortOnPriorityCreatedAt: (a, b) => {
    const DEFAULT_FOR_NULL = 0;
    const p1 = CONVERSATION_PRIORITY_ORDER[a.priority] || DEFAULT_FOR_NULL;
    const p2 = CONVERSATION_PRIORITY_ORDER[b.priority] || DEFAULT_FOR_NULL;
    if (p1 !== p2) return p2 - p1;
    return a.created_at - b.created_at;
  },

  // Espera pura: espelho do WAITING_SORT_SQL. O filtro "Aguardando mais
  // tempo" promete quem espera ha mais tempo no topo. A janela de 24h
  // mora so em sortOnWaitingWindow (fila do Modo Foco).
  // Ultimo desempate: id (display_id no payload) decrescente, igual ao SQL.
  // A paginacao por cursor depende dessa ordem total.
  sortOnWaitingSince: (a, b, sortDirection) => {
    const sortFunc = getSortOrderFunction(sortDirection);
    const desc = getSortOrderFunction('desc');
    const aIsWaiting = Boolean(a.waiting_since);
    const bIsWaiting = Boolean(b.waiting_since);

    if (aIsWaiting !== bIsWaiting) {
      return aIsWaiting ? -1 : 1;
    }
    if (aIsWaiting) {
      const waitingCmp = sortFunc(a.waiting_since, b.waiting_since);
      if (waitingCmp !== 0) return waitingCmp;
    }

    const activityCmp = desc(a.last_activity_at, b.last_activity_at);
    if (activityCmp !== 0) return activityCmp;
    return desc(Number(a.id) || 0, Number(b.id) || 0);
  },

  // Janela primeiro: regra antiga do sort de espera. So a fila do Modo
  // Foco usa, para nao desperdiçar a janela de resposta da API oficial.
  // Fora da janela afunda, nao some.
  sortOnWaitingWindow: (a, b, sortDirection) => {
    const sortFunc = getSortOrderFunction(sortDirection);
    const aIsWaiting = Boolean(a.waiting_since);
    const bIsWaiting = Boolean(b.waiting_since);

    if (aIsWaiting !== bIsWaiting) {
      return aIsWaiting ? -1 : 1;
    }
    if (!aIsWaiting) {
      return sortFunc(a.created_at, b.created_at);
    }

    const aInWindow = isInsideMessagingWindow(a);
    const bInWindow = isInsideMessagingWindow(b);
    if (aInWindow !== bInWindow) {
      return aInWindow ? -1 : 1;
    }

    return sortFunc(a.waiting_since, b.waiting_since);
  },
};

export const sortComparator = (a, b, sortKey) => {
  const [sortMethod, sortDirection] =
    SORT_OPTIONS[sortKey] || SORT_OPTIONS.last_activity_at_desc;
  return sortConfig[sortMethod](a, b, sortDirection);
};

// SiteUp: uma conversa "precisa de resposta" quando tem mensagens não lidas
// (unread_count) ou está parada aguardando atendimento (waiting_since setado).
export const isConversationAwaitingReply = chat =>
  Number(chat?.unread_count || 0) > 0 || Boolean(chat?.waiting_since);

// Entre as que aguardam resposta, a que espera há mais tempo vem primeiro
// (waiting_since/created_at = timestamps unix; cai pra created_at se não houver).
const compareByWaitingLongest = (a, b) => {
  const aWait = a.waiting_since || a.created_at || 0;
  const bWait = b.waiting_since || b.created_at || 0;
  return aWait - bWait; // crescente: quem espera há mais tempo primeiro
};

// SiteUp: "Não lidas primeiro" — flutua ao topo as conversas que precisam de
// resposta (ordenadas pela mais tempo parada); o restante segue a ordenação base
// (last_activity_at_desc = mais recentes, estilo WhatsApp). Estável com Array.sort.
export const attentionFirstComparator = (
  a,
  b,
  sortKey = 'last_activity_at_desc'
) => {
  const aAwaiting = isConversationAwaitingReply(a);
  const bAwaiting = isConversationAwaitingReply(b);
  if (aAwaiting !== bAwaiting) {
    return aAwaiting ? -1 : 1;
  }
  if (aAwaiting && bAwaiting) {
    return compareByWaitingLongest(a, b);
  }
  return sortComparator(a, b, sortKey);
};

// SiteUp: modos legados de exibição. Conversas v5 fixa last_activity_desc
// (WhatsApp-like) e não expõe seletor; chaves restantes ficam para callers internos.
export const CONVERSATION_VIEW_MODE_SORT_KEYS = {
  unread_first: 'last_activity_at_desc',
  last_activity_desc: 'last_activity_at_desc',
  last_activity_asc: 'last_activity_at_asc',
  created_desc: 'created_at_desc',
  created_asc: 'created_at_asc',
  priority: 'priority_desc',
  waiting: 'waiting_since_asc',
  waiting_window: 'waiting_window_asc',
};

// SiteUp (2026-09-23): o padrão voltou a ser "mais recentes", em todas as
// contas, por decisão do Lucas. Com "aguardando há mais tempo" como padrão, o
// cliente abria a fila e via conversas antigas no topo, e as mensagens novas
// ficavam no fim da rolagem (caso Patagonia Wild). Quem prefere a fila por
// espera escolhe no seletor, e a escolha fica salva por usuário em
// uiSettings.conversation_view_mode.
export const DEFAULT_CONVERSATION_VIEW_MODE = 'last_activity_desc';

// Retorna o comparador (a,b)=>number para um modo de exibição.
export const conversationViewComparator = mode => {
  if (mode === 'unread_first') {
    return (a, b) => attentionFirstComparator(a, b, 'last_activity_at_desc');
  }
  const sortKey =
    CONVERSATION_VIEW_MODE_SORT_KEYS[mode] || 'last_activity_at_desc';
  return (a, b) => sortComparator(a, b, sortKey);
};

// ---------------------------------------------------------------------------
// Membership kanban_items: slim (list/realtime filter) vs full (panel hydrate)
// ---------------------------------------------------------------------------
// Conversations::MembershipSerializer ships only { id, funnel_id, funnel_stage }.
// ContactPanel hydrate + kanban.* realtime can attach richer item payloads.
// Slim must never block hydrate or strip a already-hydrated full payload.

const FULL_KANBAN_ITEM_KEYS = [
  'funnel',
  'item_details',
  'checklist',
  'activities',
  'assigned_agents',
  'contact',
  'offers',
];

export const isFullKanbanItem = item => {
  if (!item || typeof item !== 'object') return false;
  return FULL_KANBAN_ITEM_KEYS.some(key => item[key] != null);
};

export const isFullKanbanItemsPayload = items =>
  Array.isArray(items) && items.length > 0 && items.some(isFullKanbanItem);

export const isSlimKanbanItemsPayload = items =>
  Array.isArray(items) && items.length > 0 && !items.some(isFullKanbanItem);

// ContactPanel: hydrate when missing OR only slim membership ids are present.
// Empty array is a confirmed "no card" from membership — do not re-fetch.
export const conversationNeedsKanbanHydrate = items => {
  if (items == null) return true;
  if (!Array.isArray(items)) return true;
  if (items.length === 0) return false;
  return isSlimKanbanItemsPayload(items);
};

// Safe merge for conversation.updated / list refresh / out-of-order membership.
// - missing incoming → keep existing
// - empty incoming → clear (membership says no cards)
// - full incoming → take incoming (hydrate / realtime upsert)
// - slim incoming over full existing → preserve rich fields, apply stage/ids
// - slim over slim/missing → take slim
export const mergeKanbanItemsMembership = (existingItems, incomingItems) => {
  if (!Array.isArray(incomingItems)) return existingItems;
  if (incomingItems.length === 0) return [];

  if (isFullKanbanItemsPayload(incomingItems)) {
    return incomingItems;
  }

  const existing = Array.isArray(existingItems) ? existingItems : [];
  if (!isFullKanbanItemsPayload(existing)) {
    return incomingItems;
  }

  const existingById = new Map(
    existing
      .filter(item => item?.id != null)
      .map(item => [Number(item.id), item])
  );

  return incomingItems.map(slim => {
    const previous = existingById.get(Number(slim.id));
    if (!previous) return slim;
    return {
      ...previous,
      id: slim.id ?? previous.id,
      funnel_id: slim.funnel_id ?? previous.funnel_id,
      funnel_stage: slim.funnel_stage ?? previous.funnel_stage,
    };
  });
};

// ConversationCard chip: resolve nested funnel or store funnel; hide when
// only funnel_id is known and no funnel name is available (no empty chip).
export const resolveKanbanChipPresentation = (kanbanItem, funnelFromStore) => {
  if (!kanbanItem) return null;

  const nested = kanbanItem.funnel;
  const nestedUsable =
    nested && typeof nested === 'object' && (nested.name || nested.stages)
      ? nested
      : null;
  const funnel = nestedUsable || funnelFromStore || null;
  if (!funnel?.name) return null;

  const stage = funnel.stages?.[kanbanItem.funnel_stage];
  const stageLabel = stage?.name || kanbanItem.funnel_stage || '';
  if (!stageLabel) return null;

  return {
    funnelName: funnel.name,
    stageLabel,
    color: stage?.color || '#64748B',
  };
};
